// src/components/SEO/StructuredData.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PATH_LANG, URL_PREFIX } from '../../langPath';

const BASE_URL = 'https://robomindsystems.ai';
// Absolute URL of a route inside the CURRENT language tree, so breadcrumbs and
// page ids on /hu/... point at /hu/... rather than at the English page.
const loc = (path = '/') => `${BASE_URL}${URL_PREFIX}${path === '/' ? '' : path}` || BASE_URL;

// type: 'homepage' | 'solution' | 'blog' | 'page'
// page: { name, path, description?, kind? } - kind is a schema.org WebPage subtype
//       (AboutPage, ContactPage, CollectionPage, ...). For 'blog' it names the post.
const StructuredData = ({ type = 'homepage', solution = null, page = null }) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language.split('-')[0];
    // Schema describes the document, so it follows the URL tree.
    const docLang = PATH_LANG || currentLang;

    // Organization Schema
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "ROBOMIND Systems",
        "legalName": "ROBOMIND Korlátolt Felelősségű Társaság",
        "alternateName": "ROBOMIND Kft.",
        "url": "https://robomindsystems.ai",
        "logo": "https://robomindsystems.ai/robomind-logo.png",
        "foundingDate": "2013",
        "vatID": "HU32824959",
        "taxID": "32824959-2-13",
        "identifier": {
            "@type": "PropertyValue",
            "name": "Company registration number",
            "value": "13-09-240793"
        },
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Leiningen Károly utca 5. 2. ajtó",
            "addressLocality": "Dunakeszi",
            "postalCode": "2120",
            "addressCountry": "HU"
        },
        "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "Customer Service",
            "email": "info@robomindsystems.ai",
            "areaServed": ["HU", "RO", "DE", "AT"],
            "availableLanguage": ["Hungarian", "Romanian", "English", "German"]
        },
        "sameAs": [
            "https://robomindsystems.ai",
            "https://www.linkedin.com/company/robomindsystems"
        ]
    };

    // LocalBusiness Schema (for Hungary)
    const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://robomindsystems.ai/#organization",
        "name": "ROBOMIND Systems",
        "image": "https://robomindsystems.ai/robomind-logo.png",
        "url": "https://robomindsystems.ai",
        "email": "info@robomindsystems.ai",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Leiningen Károly utca 5. 2. ajtó",
            "addressLocality": "Dunakeszi",
            "postalCode": "2120",
            "addressCountry": "HU"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": "47.6364",
            "longitude": "19.1381"
        },
        "areaServed": [
            { "@type": "Country", "name": "Hungary" },
            { "@type": "Country", "name": "Romania" }
        ],
        "priceRange": "$$",
        "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "17:00"
        }
    };

    // Service Schema - the current solution portfolio
    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Industrial AI and Data Solutions",
        "provider": {
            "@type": "Organization",
            "name": "ROBOMIND Systems"
        },
        "areaServed": [
            { "@type": "Country", "name": "Hungary" },
            { "@type": "Country", "name": "Romania" }
        ],
        "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Industrial AI and Data Services",
            "itemListElement": [
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Industrial Data Collection and Machine Connectivity",
                        "description": "Real-time data logging from robots, CNC machines, PLCs, sensors, and cameras using OPC UA, Modbus TCP, MQTT, and time-series databases."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Predictive Maintenance and Condition Monitoring",
                        "description": "Anomaly detection, trend analysis, and early warnings from machine data for maintenance decision support."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Industrial AI Agents",
                        "description": "On-premise AI assistants connected to production data, alarms, maintenance records, and technical documentation."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Video-Based AI Inference",
                        "description": "Continuous AI analysis of live camera streams for process monitoring, safety zones, and production event detection - processed locally."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Industrial AI Vision Systems",
                        "description": "Complete AI visual inspection systems: cameras, optics, lighting, model training, PLC and robot integration, and reporting."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Edge AI on NVIDIA Jetson",
                        "description": "AI applications developed, optimized, deployed, and maintained on NVIDIA Jetson Orin platforms for local, low-latency inference."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Industrial Dashboards",
                        "description": "Production dashboards and alerting with Grafana, InfluxDB, and custom web interfaces."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "PLC Data Collection",
                        "description": "Direct data collection from Siemens, Beckhoff, Omron, Mitsubishi, and Allen-Bradley PLCs: machine states, counters, alarms, and process values into on-premise databases and dashboards."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "AI Upgrade for Existing Camera Systems",
                        "description": "Retrofit AI detection, inspection, OCR, and monitoring onto existing IP cameras, CCTV, and machine vision stations - without replacing installed hardware."
                    }
                },
                {
                    "@type": "Offer",
                    "itemOffered": {
                        "@type": "Service",
                        "name": "Vuzix Smart Glass Solutions",
                        "description": "AI-powered applications for Vuzix Smart Glasses: hands-free operator guidance, digital work instructions, pick-by-vision logistics, and remote expert assistance with ERP/MES/WMS integration."
                    }
                }
            ]
        }
    };

    // FAQ Schema - built from the same locale keys as the visible homepage FAQ
    // (components/home/HomeFaq.js), so markup and on-page content cannot drift.
    const faqItems = t('home_faq.items', { returnObjects: true });
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "inLanguage": docLang,
        "mainEntity": (Array.isArray(faqItems) ? faqItems : []).map((item) => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": { "@type": "Answer", "text": item.a }
        }))
    };

    // WebSite Schema
    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "ROBOMIND Systems",
        "url": "https://robomindsystems.ai",
        "inLanguage": ["hu", "ro", "en", "de"]
    };

    // BreadcrumbList Schema (for internal pages)
    const crumbs = [{ name: t('nav.home'), item: loc('/') || `${BASE_URL}/` }];
    if (type === 'solution') {
        crumbs.push({ name: t('nav.solutions'), item: loc('/solutions') });
        if (solution && solution.name) {
            crumbs.push({ name: solution.name, item: loc(`/solutions/${solution.slug}`) });
        }
    } else if (type === 'blog') {
        crumbs.push({ name: t('nav.blog'), item: loc('/blog') });
        if (page && page.name) crumbs.push({ name: page.name, item: loc(page.path) });
    } else if (type === 'page' && page) {
        crumbs.push({ name: page.name, item: loc(page.path) });
    }

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "inLanguage": docLang,
        "itemListElement": crumbs.map((c, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": c.name,
            "item": c.item,
        }))
    };

    // Service schema for one specific solution page
    const solutionServiceSchema = solution && {
        "@context": "https://schema.org",
        "@type": "Service",
        "inLanguage": docLang,
        "name": solution.name,
        "description": solution.description,
        "url": loc(`/solutions/${solution.slug}`),
        "serviceType": solution.name,
        "provider": {
            "@type": "Organization",
            "name": "ROBOMIND Systems",
            "url": "https://robomindsystems.ai"
        },
        "areaServed": [
            { "@type": "Country", "name": "Hungary" },
            { "@type": "Country", "name": "Romania" },
            { "@type": "Country", "name": "Germany" }
        ],
        "availableLanguage": ["Hungarian", "Romanian", "English", "German"]
    };

    // Generic inner page (about, contact, listings, legal ...)
    const webPageSchema = page && {
        "@context": "https://schema.org",
        "@type": page.kind || "WebPage",
        "@id": `${loc(page.path)}#webpage`,
        "url": loc(page.path),
        "name": page.name,
        ...(page.description ? { "description": page.description } : {}),
        "inLanguage": docLang,
        "isPartOf": { "@type": "WebSite", "name": "ROBOMIND Systems", "url": BASE_URL },
        "publisher": { "@type": "Organization", "name": "ROBOMIND Systems", "url": BASE_URL }
    };

    // Combine schemas based on page type. The full Organization/LocalBusiness/
    // FAQ set belongs on the homepage (where the FAQ is visible); inner pages
    // carry a lighter, page-specific set.
    let schemas;
    if (type === 'solution') {
        schemas = [organizationSchema, breadcrumbSchema];
        if (solutionServiceSchema) schemas.splice(1, 0, solutionServiceSchema);
    } else if (type === 'homepage') {
        schemas = [organizationSchema, localBusinessSchema, serviceSchema, websiteSchema];
        if (faqSchema.mainEntity.length) schemas.push(faqSchema);
    } else if (type === 'blog') {
        schemas = [organizationSchema, breadcrumbSchema];
    } else {
        schemas = [breadcrumbSchema];
        if (webPageSchema) schemas.unshift(webPageSchema);
    }

    // Rendered inline (not via react-helmet-async, which no-ops on React 19).
    // JSON-LD is valid anywhere in the document; search engines and AI crawlers
    // parse it from the body just as they do from the head.
    return (
        <>
            {schemas.map((schema, index) => (
                <script
                    key={index}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
                />
            ))}
        </>
    );
};

export default StructuredData;
